/** @file instanceCore: builds the shared mutable core of a PuppetInstance (puppet, rig, meshes, defaults). */
import type { Scene } from '@babylonjs/core/scene.js';
import { prepareRig } from '@zilo/pose3d-pose/rig/prepareRig';
import { attachmentGroups } from '@zilo/pose3d-runtime-babylon/attach/attachmentGroups';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { toRuntimePuppet } from '@zilo/pose3d-runtime-babylon/bundle/toRuntimePuppet';
import { drawSet } from '@zilo/pose3d-runtime-babylon/draw/drawSet';
import { frameScratch } from '@zilo/pose3d-runtime-babylon/instance/frameScratch';
import type { InstanceCore, PuppetInstanceOptions } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';
import { RUNTIME_FNS } from '@zilo/pose3d-runtime-babylon/instance/runtimeFns';
import { jointMeshes } from '@zilo/pose3d-runtime-babylon/mesh/jointMeshes';

/** The options with their defaults filled in. */
function settings(opts: PuppetInstanceOptions): Pick<InstanceCore, 'fns' | 'depthMode' | 'ikMode' | 'blendSeconds' | 'onEvent' | 'onWarning' | 'origin'> {
  return {
    fns: opts.fns ?? RUNTIME_FNS, depthMode: opts.depthMode ?? 'slope', ikMode: opts.ikMode ?? 'auto', blendSeconds: opts.blendSeconds ?? 0.1,
    onEvent: opts.onEvent ?? (() => undefined), onWarning: opts.onWarning ?? (() => undefined), origin: opts.origin ?? [0, 0, 0],
  };
}

/**
 * The core of a new instance: the runtime puppet and rig, frame buffers, one draw set, joint meshes (unless
 * `meshes: false`), no clip (rest pose), facing = heading = `facingDeg`, the first attachment group equipped.
 */
export async function instanceCore(scene: Scene, bundle: LoadedBundle, opts: PuppetInstanceOptions): Promise<InstanceCore> {
  const puppet = toRuntimePuppet(bundle.puppet);
  const scratch = frameScratch(puppet);
  const name = `${puppet.name}#${String(scene.getUniqueId())}`;
  const facing = opts.facingDeg ?? 0;
  return {
    ...settings(opts), scene, bundle, puppet, rig: prepareRig(puppet), profile: bundle.profile, scratch,
    draw: drawSet(scene, puppet, bundle.sheets, bundle.profile, name),
    joints: opts.meshes === false ? null : await jointMeshes(scene, bundle, puppet, opts.toon ?? true),
    clock: null, events: 'fresh', facing, heading: facing, steps: null, variantMask: 0,
    weaponGroup: attachmentGroups(puppet)[0] ?? null, plants: new Map(), tick: 0, now: 0, outside: false, runs: [], lastEvents: [],
    last: { pose: scratch.pose, picks: scratch.picks, list: scratch.list, count: 0 },
  };
}
